/**
 * Sibling order for rundown rows: fractional index keys with a writer suffix
 * (PLAN §6.1, §6.2, D6).
 *
 * `ord` is an ordinary LWW field, so a move is one field write. The key comes
 * from `fractional-indexing`, which generates a string strictly between two
 * others — but that alone collides: two writers inserting into the same gap
 * with the same neighbours compute the SAME key, and the two rows then tie.
 * PLAN §6.2's fix is a **writer-id suffix**: every key this module hands out
 * ends in the writer's tag, so concurrent inserts into one gap get distinct
 * keys, ordered deterministically by tag, on every replica.
 *
 * **Appending the suffix is not automatically safe, and that is why this is a
 * module rather than a one-liner.** `k + tag` is always greater than `k`, but it
 * is only less than the upper neighbour `b` if `k` is not a prefix of `b` — and
 * the library can return such a key (between `a0` and `a1V` it returns `a1`, and
 * `a1` + `zz` sorts after `a1V`). `ordBetween` checks the bound and, when the
 * suffixed key overshoots, generates again between the candidate and `b`.
 * The same hazard is why `ordsBetween` generates its keys one at a time rather
 * than taking `generateNKeysBetween`'s batch and suffixing each: inside a batch
 * one key can be a prefix of the next.
 *
 * A tie is still possible in exactly one case — one writer, one gap, two keys
 * from the same neighbours — and that is a caller bug (it should have used the
 * first key as the new lower bound). `compareByOrd` breaks any tie by row id so
 * the materialised order is total regardless.
 */
import { BASE_62_DIGITS, generateKeyBetween } from 'fractional-indexing';

/**
 * A writer tag: base-62 digits, not ending in `0`.
 *
 * The alphabet is the key alphabet, so the suffixed key stays a valid key the
 * library will accept as a future bound. The trailing-`0` ban is the library's
 * own rule for keys (a fractional part may not end in its zero digit, because
 * `x` and `x0` would denote the same position).
 */
const WRITER_TAG = /^[0-9A-Za-z]{0,11}[1-9A-Za-z]$/;

/** How many times `ordBetween` re-generates before declaring the bounds unusable. */
const MAX_ATTEMPTS = 16;

/** Whether `tag` can be used as a writer suffix. */
export function isValidWriterTag(tag: string): boolean {
  return WRITER_TAG.test(tag);
}

function assertWriterTag(tag: string): void {
  if (!isValidWriterTag(tag)) {
    throw new Error(`Invalid writer tag ${JSON.stringify(tag)}: expected 1-12 base-62 digits not ending in 0`);
  }
}

/** FNV-1a, 32-bit: stable across processes and machines. */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * A short, stable writer tag derived from a writer id (a node id, an SPA
 * session id).
 *
 * Hashed rather than used verbatim because the tag is repeated in every key the
 * writer produces, and a UUID would triple the length of every `ord`. Two
 * writers can hash to the same tag (32 bits); the consequence is only that
 * their concurrent inserts into one gap fall back to `compareByOrd`'s row-id
 * tie-break, which is still deterministic.
 */
export function writerTagFor(writerId: string): string {
  if (writerId === '') throw new Error('writerTagFor: empty writer id');
  let n = fnv1a(writerId);
  let tag = '';
  do {
    tag = `${BASE_62_DIGITS.charAt(n % 62)}${tag}`;
    n = Math.floor(n / 62);
  } while (n > 0);
  return tag.endsWith('0') ? `${tag}1` : tag;
}

/**
 * A key strictly between `before` and `after` (null = open end), ending in
 * `writerTag`.
 */
export function ordBetween(before: string | null, after: string | null, writerTag: string): string {
  assertWriterTag(writerTag);
  if (before !== null && after !== null && before >= after) {
    throw new Error(`ordBetween: ${JSON.stringify(before)} is not below ${JSON.stringify(after)}`);
  }
  let lower = before;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const key = generateKeyBetween(lower, after);
    const candidate = `${key}${writerTag}`;
    if (after === null || candidate < after) return candidate;
    // `key` is a prefix of `after`, so the suffix pushed it past the bound.
    // Anything generated between `key` and `after` extends further and
    // diverges from `after` below it.
    lower = key;
  }
  throw new Error(
    `ordBetween: no key between ${JSON.stringify(before)} and ${JSON.stringify(after)} ` +
      `for writer ${JSON.stringify(writerTag)} after ${String(MAX_ATTEMPTS)} attempts`,
  );
}

/**
 * `count` ascending keys strictly between `before` and `after`, for inserting
 * or moving a block of rows as one batch (PLAN §6.2: "the rows end up adjacent
 * in the same relative order").
 */
export function ordsBetween(before: string | null, after: string | null, count: number, writerTag: string): string[] {
  if (!Number.isInteger(count) || count < 0) throw new Error(`ordsBetween: invalid count ${String(count)}`);
  const keys: string[] = [];
  let lower = before;
  for (let i = 0; i < count; i += 1) {
    const key = ordBetween(lower, after, writerTag);
    keys.push(key);
    lower = key;
  }
  return keys;
}

/**
 * Whether `ord` is a key the ordering functions accept as a bound: a
 * `fractional-indexing` key (which every `ordBetween` result is — the writer
 * suffix is drawn from the key alphabet). Checked at ingress (PLAN §6.4), so a
 * malformed `ord` from one client cannot make every later insert next to that
 * row throw on every replica.
 *
 * The alphabet check comes first because the library validates the integer
 * head and the trailing digit but not every character (`'a0 '` passes it).
 */
const ORD_ALPHABET = /^[0-9A-Za-z]{2,200}$/;

export function isValidOrd(ord: string): boolean {
  if (!ORD_ALPHABET.test(ord)) return false;
  try {
    generateKeyBetween(ord, null);
    return true;
  } catch {
    return false;
  }
}

/** A row as the sibling sort sees it. */
export type Ordered = { readonly id: string; readonly ord: string };

/**
 * Sibling order: by `ord`, then by row id. Plain code-unit comparison, which is
 * the order `fractional-indexing` generates in (its alphabet is ascending
 * ASCII) — never `localeCompare`, which would reorder keys by locale rules.
 */
export function compareByOrd(a: Ordered, b: Ordered): number {
  if (a.ord !== b.ord) return a.ord < b.ord ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}
